// Pure option handling behind the Select control (components/controls).

// Options whose label contains every word of the query, ignoring case,
// accents and punctuation ("cez" finds "ČEZ", "eon" finds "E.ON", "alen ph"
// finds "Alensa - AI agent Phase1"). `keep` values stay listed whatever the
// query — the "All …" row.
export function filterOptions(options, query, keep = []) {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return options;
  return options.filter((option) => {
    if (keep.includes(option.value)) return true;
    const label = fold(option.label);
    return words.every((word) => label.includes(word));
  });
}

export function fold(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .toLowerCase();
}

// The next enabled option from `index` in `step` direction (+1 / -1), or the
// current index when there is none.
export function nextEnabled(options, index, step) {
  for (let i = index + step; i >= 0 && i < options.length; i += step) {
    if (!options[i].disabled) return i;
  }
  return index;
}

export function firstEnabled(options) {
  return nextEnabled(options, -1, 1);
}

// Where a search lands in the filtered list `shown`: the first enabled option
// the query matches that is not a `keep` row — so typing "al" and pressing
// Enter picks "Alensa", not "All clients". A kept row only when it is the sole
// match; the first enabled row when the query is empty or nothing matches.
export function firstMatchIndex(shown, query, keep = []) {
  if (!fold(query).trim()) return firstEnabled(shown);
  const matched = new Set(filterOptions(shown, query));
  const isMatch = (option) => !option.disabled && matched.has(option);
  let index = shown.findIndex(
    (option) => isMatch(option) && !keep.includes(option.value),
  );
  if (index < 0) index = shown.findIndex(isMatch);
  return index >= 0 ? index : firstEnabled(shown);
}

export function lastEnabled(options) {
  return nextEnabled(options, options.length, -1);
}

// Type-ahead on a closed or unsearchable list: the first enabled option after
// `from` whose label starts with `prefix`, wrapping around; -1 when none.
export function typeAheadIndex(options, prefix, from) {
  const needle = fold(prefix);
  if (!needle) return -1;
  for (let n = 1; n <= options.length; n += 1) {
    const i = (from + n) % options.length;
    if (!options[i].disabled && fold(options[i].label).startsWith(needle))
      return i;
  }
  return -1;
}
